import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { useState, useRef, useEffect } from 'react';

export default function HeroSection() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Aesthetic%20minimalist%20flat%20lay%20of%20designer%20sneakers%20and%20folded%20clothing%20on%20warm%20cream%20textured%20paper%20background%2C%20soft%20natural%20daylight%20from%20left%20window%2C%20editorial%20fashion%20product%20photography%2C%20warm%20neutral%20beige%20and%20cream%20tones%2C%20organic%20composition%20with%20dried%20flowers%20and%20linen%20fabric%2C%20elegant%20clean%20aesthetic%2C%20high%20detail%2C%20upscale%20minimalist%20style&width=1800&height=1200&seq=yoybuy-hero-bg&orientation=landscape')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40" />

      <div className="relative z-10 w-full px-4 md:px-6 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row items-center lg:items-end gap-12 lg:gap-0">
          <div className="lg:w-[60%] flex flex-col items-start">
            <div className="flex items-center gap-4 mb-8">
              <div className="flex -space-x-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-background-50 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20of%20young%20person%20against%20soft%20cream%20studio%20background%2C%20warm%20natural%20lighting%2C%20neutral%20expression%2C%20clean%20minimalist%20style%2C%20editorial%20quality%2C%20high%20detail&width=100&height=100&seq=yoybuy-avatar-01&orientation=squarish"
                    alt="User 1"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-background-50 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20of%20young%20woman%20against%20soft%20cream%20studio%20background%2C%20warm%20natural%20lighting%2C%20friendly%20expression%2C%20clean%20minimalist%20style%2C%20editorial%20quality%2C%20high%20detail&width=100&height=100&seq=yoybuy-avatar-02&orientation=squarish"
                    alt="User 2"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-background-50 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20headshot%20of%20young%20man%20against%20soft%20cream%20studio%20background%2C%20warm%20natural%20lighting%2C%20confident%20expression%2C%20clean%20minimalist%20style%2C%20editorial%20quality%2C%20high%20detail&width=100&height=100&seq=yoybuy-avatar-03&orientation=squarish"
                    alt="User 3"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <p className="text-background-50/90 text-sm md:text-base whitespace-nowrap">
                {t('hero.socialProof', 'Более 10 000 довольных покупателей')}
              </p>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading text-background-50 leading-tight">
              <span className="font-bold block">{t('hero.title1', 'YOYBUY Spreadsheet')}</span>
              <span className="font-light block mt-2">{t('hero.title2', 'Удобная система покупок из Китая')}</span>
            </h1>

            <div className="flex flex-col sm:flex-row items-start gap-4 mt-10">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-foreground-950 font-semibold text-sm whitespace-nowrap transition-all duration-300 hover:bg-background-100 hover:scale-105 cursor-pointer"
              >
                {t('hero.ctaPrimary', 'YOYBUY Sheet')}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-background-50/60 text-background-50 font-semibold text-sm whitespace-nowrap transition-all duration-300 hover:bg-background-50/10 hover:scale-105 cursor-pointer"
              >
                {t('hero.ctaSecondary', 'Buy It Now')}
              </a>
            </div>
          </div>

          <div className="lg:w-[40%] flex flex-col items-start lg:items-end lg:pb-4">
            <div ref={dropdownRef} className="relative w-full max-w-sm">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between py-4 px-0 border-b border-background-50/50 text-background-50/90 hover:text-background-50 transition-colors duration-200 cursor-pointer"
              >
                <span className="text-base font-medium whitespace-nowrap">
                  {t('selectCategory', 'Выберите категорию')}
                </span>
                <span className="w-5 h-5 flex items-center justify-center">
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line"></i>
                  )}
                </span>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 w-full mt-2 bg-background-50 rounded-xl overflow-hidden z-20 max-h-[340px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameEn}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setDropdownOpen(false)}
                      className="block px-5 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
              <p className="mt-5 text-background-50/75 text-sm md:text-base leading-relaxed max-w-sm">
                {t('hero.subtitle', 'Сравнивайте товары, оформляйте заказы и экономьте на доставке через удобные таблицы')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}